import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { useNotification } from '../components/Notifications';

export default function Subscription() {
    const [plans, setPlans] = useState([]);
    const [current, setCurrent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { 
        setLoading(true);
        Promise.all([fetchPlans(), fetchCurrent()]).finally(() => setLoading(false));
    }, []);
    // On mount, handle potential redirect from Flutterwave containing tx_ref
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tx = params.get('tx_ref') || params.get('txref') || params.get('reference');
        if (tx) {
            (async () => {
                try {
                    await apiFetch('/api/payments/confirm', { method: 'POST', body: JSON.stringify({ tx_ref: tx }), skipTenant: true });
                } catch (e) {
                    // ignore
                }
                // refresh current plan
                await fetchCurrent();
                // remove query params from URL
                window.history.replaceState({}, document.title, window.location.pathname);
            })();
        }
    }, []);

    const notify = useNotification();

    const upgrade = async (id) => {
        try {
            // Build tenant-aware return URL so user is redirected back to the correct tenant path
            const storedTenant = localStorage.getItem('tenant');
            let tenant = storedTenant || null;
            if (!tenant) {
                const seg = window.location.pathname.replace(/^\/+|\/+$/g, '').split('/');
                if (seg.length >= 2 && seg[0].toLowerCase() === 't' && seg[1]) {
                    tenant = seg[1];
                } else if (seg.length >= 1 && seg[0]) {
                    const nonTenantPrefixes = ['login','register','forgot-password','verify-email','reset-password','api','static','assets','favicon.ico'];
                    const top = seg[0].toLowerCase();
                    if (!nonTenantPrefixes.includes(top)) tenant = seg[0];
                }
            }

            const returnUrl = tenant ? `${window.location.origin}/${tenant}/subscription` : `${window.location.origin}/subscription`;

            const body = { PlanId: id, ReturnUrl: returnUrl };
            const res = await apiFetch('/api/payments/create', { method: 'POST', body: JSON.stringify(body) });
            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                notify('error', (parsed && parsed.message) || 'Failed to start payment');
                return;
            }
            const data = await res.json();
            const url = data?.checkoutUrl || data?.checkout_url || data?.data?.checkoutUrl;
            if (url) {
                window.location.href = url;
            } else {
                notify('error', 'Payment service returned no checkout URL');
            }
        } catch (e) {
            console.error(e);
            notify('error', 'Network error');
        }
    };

    async function fetchPlans() {
        try {
            const res = await apiFetch('/api/subscription/plans');
            if (!res.ok) return;
            const data = await res.json();
            setPlans(data?.plans || []);
        } catch (e) { console.error(e); }
    }

    async function fetchCurrent() {
        try {
            const res = await apiFetch('/api/subscription');
            if (!res.ok) return;
            const data = await res.json();
            setCurrent(data?.plan || null);
        } catch (e) { console.error(e); }
    }

    // Helper function to get features based on plan name
    const getPlanFeatures = (planName) => {
        const lower = (planName || '').toLowerCase();
        if (lower.includes('premium')) {
            return [
                'Admin and multiple users',
                'Unlimited items',
                'Dedicated support',
                'SLA'
            ];
        } else if (lower.includes('classic')) {
            return [
                'Admin and single user',
                '50 items limit',
                'Priority support'
            ];
        } else if (lower.includes('basic')) {
            return [
                'Single user',
                '10 items limit',
                'Community support'
            ];
        }
        return [];
    };

    return (
        <div className="col-span-12 lg:col-span-10 flex justify-center">
            <div className="w-11/12 max-w-6xl py-8">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold">Subscription Plans</h2>
                    {current && (
                        <div className="text-sm text-gray-600">Current plan: <strong>{current.name || current.Name}</strong></div>
                    )}
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="p-6 bg-white rounded-lg border border-gray-200 text-center text-gray-600">No plans available. Try refreshing or check API connectivity.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
                        {plans.map(p => {
                            const id = p.planId || p.PlanId;
                            const name = p.name || p.Name;
                            const price = p.monthlyPrice ?? p.MonthlyPrice ?? 0;
                            const priceStr = new Intl.NumberFormat('en-NG').format(price);
                            const itemLimit = p.itemLimit ?? p.ItemLimit;
                            let features = p.featuresJson || p.FeaturesJson || '';
                            // replace placeholder ? with Naira symbol if present
                            if (features && typeof features === 'string') {
                                features = features.replace(/\?/g, '₦');
                            }
                            const isCurrent = current && ((current.planId && current.planId === id) || (current.PlanId && current.PlanId === id) || (current.planId === id));

                            return (
                                <div 
                                    key={id} 
                                    className={`w-full max-w-sm bg-white rounded-lg border-2 transition-all duration-300 hover:shadow-lg ${
                                        isCurrent 
                                            ? 'border-indigo-600 shadow-md' 
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    {/* Header Section */}
                                    <div className="px-6 pt-6 pb-4">
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900">{name}</h3>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {itemLimit ? `Up to ${itemLimit} items` : 'Unlimited items'}
                                                </p>
                                            </div>
                                            {isCurrent && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                                                    ✓ Current
                                                </span>
                                            )}
                                        </div>

                                        {/* Price Section */}
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-bold text-gray-900">₦{priceStr}</span>
                                            <span className="text-gray-500 font-medium">/month</span>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200"></div>

                                    {/* Features Section */}
                                    <div className="px-6 py-5">
                                        <div className="text-sm text-gray-700 mb-2">
                                            {(() => {
                                                const planFeatures = getPlanFeatures(name);
                                                
                                                // If we have predefined features for this plan, use them
                                                if (planFeatures.length > 0) {
                                                    return (
                                                        <ul className="space-y-2">
                                                            {planFeatures.map((feature, i) => (
                                                                <li key={i} className="flex items-start gap-3 text-gray-600">
                                                                    <span className="text-indigo-600 font-bold mt-0.5">✓</span>
                                                                    <span>{feature}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    );
                                                }
                                                
                                                // Fallback to original features parsing
                                                if (features) {
                                                    try {
                                                        const parsed = JSON.parse(features);
                                                        if (parsed && typeof parsed === 'object') {
                                                            if (parsed.description) {
                                                                return <div className="text-gray-600">{parsed.description.replace(/\?/g, '₦')}</div>;
                                                            }
                                                            if (Array.isArray(parsed)) return (
                                                                <ul className="space-y-2">
                                                                    {parsed.map((it, i) => (
                                                                        <li key={i} className="flex items-start gap-3 text-gray-600">
                                                                            <span className="text-indigo-600 font-bold mt-0.5">✓</span>
                                                                            <span>{(''+it).replace(/\?/g, '₦')}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            );
                                                            return <div className="text-gray-600">{Object.values(parsed).map(v => (''+v).replace(/\?/g, '₦')).join(' • ')}</div>;
                                                        }
                                                    } catch (e) {
                                                        // not json
                                                    }
                                                    return <div className="text-gray-600">{features.replace(/\?/g, '₦')}</div>;
                                                }
                                                
                                                // Default fallback features
                                                return (
                                                    <ul className="space-y-2">
                                                        <li className="flex items-start gap-3 text-gray-600">
                                                            <span className="text-indigo-600 font-bold mt-0.5">✓</span>
                                                            <span>Access to core features</span>
                                                        </li>
                                                        {itemLimit ? (
                                                            <li className="flex items-start gap-3 text-gray-600">
                                                                <span className="text-indigo-600 font-bold mt-0.5">✓</span>
                                                                <span>Store up to <strong>{itemLimit}</strong> items</span>
                                                            </li>
                                                        ) : (
                                                            <li className="flex items-start gap-3 text-gray-600">
                                                                <span className="text-indigo-600 font-bold mt-0.5">✓</span>
                                                                <span>Unlimited items</span>
                                                            </li>
                                                        )}
                                                        {name.toLowerCase() !== 'basic' && (
                                                            <li className="flex items-start gap-3 text-gray-600">
                                                                <span className="text-indigo-600 font-bold mt-0.5">✓</span>
                                                                <span>Invite and manage users</span>
                                                            </li>
                                                        )}
                                                    </ul>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200"></div>

                                    {/* Button Section */}
                                    <div className="px-6 py-4">
                                        <button
                                            className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-colors duration-200 ${
                                                isCurrent 
                                                    ? 'bg-gray-100 text-gray-700 cursor-not-allowed' 
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800'
                                            }`}
                                            disabled={isCurrent}
                                            onClick={() => !isCurrent && upgrade(id)}
                                        >
                                            {isCurrent ? '✓ Current Plan' : 'Upgrade to ' + name}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
